import React, { useMemo, useState } from 'react';
import { withAuth, WithAuthProps } from 'components/header/hoc/with-auth';
import { UserMenuContent } from 'components/header/components/profile/components/user-menu/user-menu-content';
import { useStoreon } from 'storeon/react';
import { isUserAuth } from 'selectors/cookie';
import { State } from 'store/types';
import { UserCircleIcon, UserIcon } from '@fronton/icons-react';
import { Badge, Modal } from 'fronton-react';
import { useShoppingList, useMobileMenuAnalyticsEvent } from 'components/header/hooks';
import { useTranslation } from 'react-i18next';
import { patchUrlByLocale } from 'utils';
import { MobileMenuLink } from './mobile-menu-link';
import { menuContent, badge, ProIconWrapper } from './styles';
import { EntryUserMenu } from './entry-user-menu';

import { ProIcon } from 'components/icons';
import {
  LOYALTY_PRO_ICON_QA,
  NEED_ACTION_BADGE_QA,
} from '../profile/components/user/constants/qa-constants';
import { useLoyaltyData } from 'common/hooks/use-loyalty-data';
import { isB2BUserAuthedSSR } from 'utils/b2b-utils';
import { ChangeOrganizationPopup } from '../profile/components/b2b-user-menu/change-organization-popup';
import { B2BOrganizationsStore } from 'reducers/b2b-organization';
import { B2B_ORGANIZATION_ACTIONS } from 'actions/b2b-organization';
import { Content as B2BUserContent } from '../profile/components/b2b-user-menu/menu-content';

interface MobileUserMenuProps extends WithAuthProps {
  href: string;
  shoppingListUrl: string;
  isB2BEnabled: boolean;
}

export const DATA_QA_MOBILE_MENU_AUTH_LINK = 'mobile-menu-auth-link';

export const MobileUserMenuBase = (props: MobileUserMenuProps) => {
  const { href, shoppingListUrl, isB2BEnabled, ...rest } = props;
  const { cookies, isBot } = useStoreon<State>('cookies', 'isBot');

  const { B2BOrganizations, dispatch } = useStoreon<B2BOrganizationsStore>('B2BOrganizations');

  const { fetchedLoyaltyAccountSuccess, loyaltyAccountIdentifier, keycardPro } = useLoyaltyData();

  const [isVisiblePopup, setVisiblePopup] = useState(false);

  const isAuth = isUserAuth(cookies);
  const isB2BAuthed = isB2BUserAuthedSSR(cookies);

  const { t } = useTranslation();

  const { shoppingListCount } = useShoppingList();

  const [show, setShow] = useState(false);

  const onEnterClick = useMobileMenuAnalyticsEvent('Вход');
  const onProfileClick = useMobileMenuAnalyticsEvent('Профиль');

  const onApplyButtonClick = (organizationNumber: string) => {
    dispatch(B2B_ORGANIZATION_ACTIONS.setCurrentOrganizationNumber, organizationNumber);

    setVisiblePopup(false);
  };

  const showChangeOrganizationPopup = () => {
    setShow(false);
    setVisiblePopup(true);
  };

  const onClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    setShow(true);

    if (isAuth) {
      onProfileClick();
    } else {
      onEnterClick();
    }
  };

  const getBadge = useMemo(() => {
    if (!fetchedLoyaltyAccountSuccess) {
      return <></>;
    }

    const showBadge = !loyaltyAccountIdentifier;
    const showProCardLabel = keycardPro;

    if (showBadge) {
      return <Badge data-qa={NEED_ACTION_BADGE_QA} size="xs" value={1} className={badge} />;
    }

    if (showProCardLabel) {
      return (
        <ProIconWrapper data-qa={LOYALTY_PRO_ICON_QA}>
          <ProIcon />
        </ProIconWrapper>
      );
    }

    return <></>;
  }, [fetchedLoyaltyAccountSuccess, loyaltyAccountIdentifier, keycardPro]);

  const isMenuLinkEnabled = isB2BAuthed || isAuth;

  return (
    <>
      {!isBot && (
        <MobileMenuLink
          icon={isMenuLinkEnabled ? UserCircleIcon : UserIcon}
          href={href}
          text={isMenuLinkEnabled ? t('header.main.profile') : t('header.main.enter')}
          getIsActive={() => false}
          data-qa={DATA_QA_MOBILE_MENU_AUTH_LINK}
          onClick={onClick}
          topBadge={getBadge}
        />
      )}
      <ChangeOrganizationPopup
        visible={isVisiblePopup}
        list={B2BOrganizations.list}
        currentOrganizationNumber={B2BOrganizations.currentOrganizationNumber || ''}
        isMobileView={true}
        onApplyClick={onApplyButtonClick}
        onClose={() => setVisiblePopup(false)}
      />
      {isB2BAuthed ? (
        <Modal show={show} onClose={() => setShow(false)} variant="curtain">
          <div className={menuContent}>
            <B2BUserContent
              onLogout={props.logout}
              organizationCount={B2BOrganizations.list.length}
              organizationName={B2BOrganizations.currentOrganizationName}
              showChangeOrganizationPopup={showChangeOrganizationPopup}
            />
          </div>
        </Modal>
      ) : (
        <Modal show={show} onClose={() => setShow(false)} variant="curtain">
          <div className={menuContent}>
            {isAuth ? (
              <UserMenuContent
                shoppingListUrl={patchUrlByLocale({ url: shoppingListUrl })}
                shoppingListCount={shoppingListCount}
                {...rest}
              />
            ) : (
              <EntryUserMenu loginPageUrl={href} isB2BEnabled={isB2BEnabled} />
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export const MobileUserMenu = withAuth(MobileUserMenuBase);
